import React, { useState, useEffect } from 'react';
import { X, Copy, CheckCircle, Loader, QrCode, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PixCheckoutModal = ({ isOpen, onClose, orderData, subdomain, onSuccess }) => {
  const [qrCode, setQrCode] = useState('');
  const [qrCodeBase64, setQrCodeBase64] = useState('');
  const [paymentId, setPaymentId] = useState('');
  const [orderId, setOrderId] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState('creating'); // creating, pending, checking, approved, error
  const [error, setError] = useState('');
  const [pollingInterval, setPollingInterval] = useState(null);

  useEffect(() => {
    if (isOpen && orderData) {
      createOrderAndPayment();
    }
    
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [isOpen, orderData]);

  useEffect(() => {
    if (status === 'checking' && paymentId && orderId) {
      // Start polling for payment status
      const interval = setInterval(() => {
        checkPaymentStatus();
      }, 3000); // Check every 3 seconds

      setPollingInterval(interval);

      return () => clearInterval(interval);
    }
  }, [status, paymentId, orderId]);

  const createOrderAndPayment = async () => {
    try {
      setStatus('creating');
      setError('');

      // Step 1: Create order in backend
      const orderResponse = await axios.post(
        `${API}/public/menu/${subdomain}/orders`,
        orderData
      );

      if (!orderResponse.data || !orderResponse.data.order_number) {
        throw new Error('Erro ao criar pedido');
      }

      const createdOrderId = orderResponse.data.order_id || orderResponse.data.id;
      const createdOrderNumber = orderResponse.data.order_number;
      setOrderId(createdOrderId);
      setOrderNumber(createdOrderNumber);

      // Step 2: Create PIX payment
      const paymentResponse = await axios.post(
        `${API}/mercadopago/create-pix-payment`,
        {
          order_id: createdOrderId,
          amount: orderData.total,
          description: `Pedido ${orderResponse.data.order_number} - ${orderData.customer_name}`,
          payer_email: orderData.customer_email || 'cliente@email.com'
        }
      );

      if (paymentResponse.data.success) {
        setQrCode(paymentResponse.data.qr_code);
        setQrCodeBase64(paymentResponse.data.qr_code_base64);
        setPaymentId(paymentResponse.data.payment_id);
        setStatus('checking');
      } else {
        setError('Erro ao criar pagamento PIX');
        setStatus('error');
      }
    } catch (err) {
      console.error('Error creating order/payment:', err);
      setError(
        err.response?.data?.detail || 
        'Erro ao processar pedido. A loja pode não ter configurado o Mercado Pago.'
      );
      setStatus('error');
    }
  };

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(
        `${API}/mercadopago/payment-status/${paymentId}?order_id=${orderId}`
      );

      if (response.data.status === 'approved') {
        setStatus('approved');
        if (pollingInterval) {
          clearInterval(pollingInterval);
        }
        
        // Wait 2 seconds then call success with order data
        setTimeout(() => {
          if (onSuccess) {
            // Passar dados completos do pedido para a tela de sucesso
            const confirmedOrder = {
              ...orderData,
              order_id: orderId,
              order_number: orderNumber,
              payment_status: 'approved',
              payment_method: 'pix_mp'
            };
            onSuccess(confirmedOrder);
          }
          // Não chamar onClose aqui - deixar a tela de sucesso gerenciar
        }, 2000);
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
      // Continue polling even on error
    }
  };

  const copyToClipboard = () => {
    if (qrCode) {
      navigator.clipboard.writeText(qrCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Pagamento PIX</h2>
            <p className="text-gray-600 text-sm mt-1">
              R$ {orderData?.total?.toFixed(2)}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {status === 'creating' && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader className="w-12 h-12 text-emerald-500 animate-spin mb-4" />
              <p className="text-gray-700 font-semibold">Gerando pagamento PIX...</p>
              <p className="text-gray-500 text-sm mt-2">Aguarde um momento</p>
            </div>
          )}

          {status === 'error' && (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-6 h-6 text-red-500" />
              </div>
              <p className="text-red-600 font-semibold mb-2">Erro ao criar pagamento</p>
              <p className="text-gray-600 text-sm">{error}</p>
              <button
                onClick={createOrderAndPayment}
                className="mt-4 px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              >
                Tentar Novamente
              </button>
            </div>
          )}

          {(status === 'checking' || status === 'approved') && (
            <>
              {/* QR Code */}
              {qrCodeBase64 && (
                <div className="flex flex-col items-center">
                  <div className="bg-white p-4 rounded-2xl shadow-xl border-2 border-gray-200">
                    <img
                      src={`data:image/png;base64,${qrCodeBase64}`}
                      alt="QR Code PIX"
                      className="w-64 h-64"
                    />
                  </div>
                  <p className="text-gray-600 text-sm mt-4 text-center">
                    Escaneie o QR Code com seu aplicativo bancário
                  </p>
                </div>
              )}

              {/* PIX Code */}
              {qrCode && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Ou copie o código PIX:
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={qrCode}
                      readOnly
                      className="flex-1 bg-gray-100 border border-gray-300 rounded-lg px-4 py-3 text-gray-800 text-sm font-mono"
                    />
                    <button
                      onClick={copyToClipboard}
                      className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap"
                    >
                      {copied ? (
                        <>
                          <CheckCircle className="w-5 h-5" />
                          Copiado!
                        </>
                      ) : (
                        <>
                          <Copy className="w-5 h-5" />
                          Copiar
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Status */}
              {status === 'checking' && (
                <div className="bg-emerald-50 border-2 border-blue-200 rounded-xl p-6 text-center">
                  <div className="flex items-center justify-center mb-4">
                    <Loader className="w-8 h-8 text-emerald-500 animate-spin" />
                  </div>
                  <p className="text-gray-800 font-semibold mb-2">Aguardando pagamento...</p>
                  <p className="text-gray-600 text-sm">
                    Estamos verificando seu pagamento automaticamente
                  </p>
                </div>
              )}

              {status === 'approved' && (
                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <p className="text-green-600 font-bold text-xl mb-2">Pagamento Aprovado!</p>
                  <p className="text-gray-600 text-sm">
                    Seu pedido foi confirmado com sucesso
                  </p>
                </div>
              )}

              {/* Instructions */}
              <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                <h3 className="text-gray-800 font-semibold text-sm mb-3">Como pagar:</h3>
                <div className="space-y-2 text-gray-600 text-sm">
                  <p>1. Abra seu aplicativo bancário</p>
                  <p>2. Escolha pagar com PIX</p>
                  <p>3. Escaneie o QR Code ou cole o código</p>
                  <p>4. Confirme o pagamento</p>
                  <p className="text-green-600 font-semibold mt-3">
                    ✓ O pagamento será confirmado automaticamente
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {status !== 'approved' && (
          <div className="p-6 border-t border-gray-200">
            <button
              onClick={handleClose}
              className="w-full px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-medium"
            >
              Fechar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PixCheckoutModal;
